import { useTranslation } from "react-i18next";
import "./Pagination.css";

interface PaginationProps {
  /** Current page, 0-based */
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

/**
 * Numbered pagination controls: prev/next + windowed page numbers
 * with first/last and ellipsis when there are many pages.
 * Renders nothing when there is a single page or less.
 */
export function Pagination({ page, totalPages, onPageChange, disabled = false }: PaginationProps) {
  const { t } = useTranslation();

  if (totalPages <= 1) return null;

  const getPageNumbers = (): (number | "ellipsis")[] => {
    const windowSize = 2; // pages on each side of the current page
    const pages = new Set<number>([0, totalPages - 1]);
    for (let i = page - windowSize; i <= page + windowSize; i++) {
      if (i >= 0 && i < totalPages) pages.add(i);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const result: (number | "ellipsis")[] = [];
    let prev = -1;
    for (const p of sorted) {
      if (prev !== -1 && p - prev > 1) result.push("ellipsis");
      result.push(p);
      prev = p;
    }
    return result;
  };

  return (
    <nav className="pagination" aria-label={t("admin.paginationLabel", "Pagination")}>
      <button
        type="button"
        className="pagination__btn pagination__btn--nav"
        onClick={() => onPageChange(page - 1)}
        disabled={disabled || page === 0}
        aria-label={t("admin.paginationPrev", "Previous page")}
      >
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      {getPageNumbers().map((item, index) =>
        item === "ellipsis" ? (
          <span key={`ellipsis-${index}`} className="pagination__ellipsis">
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            className={`pagination__btn ${item === page ? "pagination__btn--active" : ""}`}
            onClick={() => onPageChange(item)}
            disabled={disabled}
            aria-label={t("admin.paginationPage", "Page {{page}}", { page: item + 1 })}
            aria-current={item === page ? "page" : undefined}
          >
            {item + 1}
          </button>
        )
      )}

      <button
        type="button"
        className="pagination__btn pagination__btn--nav"
        onClick={() => onPageChange(page + 1)}
        disabled={disabled || page >= totalPages - 1}
        aria-label={t("admin.paginationNext", "Next page")}
      >
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </nav>
  );
}
